import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { DangerConfirmButton, PrimaryButton } from 'Shared/editor/buttons';

/**
 * Announces the in-flight write: the disabled attribute blurs the pressed
 * button mid-request, so its label change alone is silent for screen
 * readers. Mount it at the editor's root, never in the action bar — it has
 * to outlive the write and stay outside any region the page makes `inert`,
 * which hides a nested live region from assistive tech. A region that
 * mounts, remounts, or arrives with the write announces nothing.
 */
export function WriteStatusAnnouncer({
   isSaving,
   isDeleting = false,
   isApproving = false,
}: {
   isSaving: boolean;
   isDeleting?: boolean;
   isApproving?: boolean;
}) {
   return (
      <span role="status" className="sr-only">
         {isSaving
            ? _js('Saving...')
            : isDeleting
              ? _js('Deleting...')
              : isApproving
                ? _js('Approving...')
                : ''}
      </span>
   );
}

/**
 * Sits as the last child of a SectionCard: the negative margins cancel the
 * card's padding so the bar spans its full width and settles into its
 * bottom edge, sticking to the viewport while the card scrolls. Exported
 * for the guide editors, which build their own bar bodies but need the same
 * bleed, and it is coupled to SectionCard's padding.
 */
export const BAR_CLASSES =
   'sticky bottom-0 z-10 -mx-6 -mb-8 mt-6 rounded-b-2xl border-t border-gray-200 bg-white px-6 py-3 max-md:-mx-4 max-md:-mb-4 max-md:rounded-b-none max-md:px-4';

/**
 * The wiki editors' action bar: optional delete on the left, optional
 * preview plus save on the right.
 */
export function EditorActionBar({
   canDelete,
   deleteLabel,
   deleteTitle,
   deleteDescription,
   isSaving,
   isDeleting,
   isPreviewing = false,
   isRendering = false,
   saveDisabled = false,
   onSave,
   onDelete,
   onPreview,
   onRender,
   onPreviewIntent,
   onRenderIntent,
}: {
   canDelete: boolean;
   deleteLabel: string;
   deleteTitle: string;
   deleteDescription: string;
   isSaving: boolean;
   isDeleting: boolean;
   isPreviewing?: boolean;
   isRendering?: boolean;
   /** Extra save blockers beyond the write lock (e.g. image processing). */
   saveDisabled?: boolean;
   onSave: () => void;
   onDelete: () => void;
   onPreview?: () => void;
   /** Opens the rendered-page preview; omitted where there isn't one. */
   onRender?: () => void;
   /**
    * Pointer or keyboard intent on a button, before the click. The pages
    * start the fetch here so the click has less to wait for.
    */
   onPreviewIntent?: () => void;
   onRenderIntent?: () => void;
}) {
   return (
      <div className={BAR_CLASSES}>
         <div className="flex flex-wrap items-center justify-between gap-2">
            {canDelete ? (
               <DangerConfirmButton
                  label={deleteLabel}
                  pendingLabel={_js('Deleting...')}
                  title={deleteTitle}
                  description={deleteDescription}
                  isPending={isDeleting}
                  disabled={isSaving}
                  disabledReason={isSaving ? _js('Wait for the save to finish.') : undefined}
                  triggerTestId="editor-delete"
                  confirmTestId="editor-delete-confirm"
                  onConfirm={onDelete}
               />
            ) : (
               <span />
            )}
            <div className="flex items-center gap-2">
               {/* Two buttons, not one control with a mode: each answers a
                   different question (what did I change, how will it look),
                   so either is one click away. Both are locked during
                   save/delete: those paths redirect on success, and a
                   preview racing location.assign renders against a page
                   that's already leaving. */}
               {/* No aria-label and no title: the text is the name at every
                   width (it is only visually hidden below sm), a fixed label
                   would hide the pending text from a screen reader, and a
                   title would repeat the label as a tooltip. */}
               {onPreview && (
                  <button
                     className="button-md button-outline"
                     disabled={isPreviewing || isRendering || isSaving || isDeleting}
                     onClick={onPreview}
                     onPointerEnter={onPreviewIntent}
                     onFocus={onPreviewIntent}
                  >
                     <FaIcon name="file-lines" className="size-4 sm:hidden" aria-hidden />
                     <span className="max-sm:sr-only">
                        {isPreviewing ? _js('Loading changes...') : _js('Show changes')}
                     </span>
                  </button>
               )}
               {onRender && (
                  <button
                     className="button-md button-outline"
                     disabled={isPreviewing || isRendering || isSaving || isDeleting}
                     onClick={onRender}
                     onPointerEnter={onRenderIntent}
                     onFocus={onRenderIntent}
                     data-testid="editor-preview"
                  >
                     <FaIcon name="eye" className="size-4 sm:hidden" aria-hidden />
                     <span className="max-sm:sr-only">
                        {isRendering ? _js('Loading preview...') : _js('Preview')}
                     </span>
                  </button>
               )}
               <PrimaryButton
                  isPending={isSaving}
                  disabled={isDeleting || saveDisabled}
                  onClick={onSave}
                  data-testid="editor-save"
               >
                  {isSaving ? _js('Saving...') : _js('Save')}
               </PrimaryButton>
            </div>
         </div>
      </div>
   );
}
